import { createRef, LitElement, css, html, ref } from '@lit'
import styles from '@nova/styles'

class NovaSearchResult extends LitElement {
  static properties = {
    searchData: { type: String },
    searchTerm: { type: String },
  }

  constructor() {
    super()
    this.searchData = ''
    this.searchTerm = ''
    this.searchResultContainerRef = createRef()
  }

  updated(changedProperties) {
    super.updated(changedProperties)
    if (this.searchData === '') {
      const container = this.searchResultContainerRef.value
      if (container) container.innerHTML = ''
      return
    }

    if (changedProperties.has('searchData')) {
      const parser = new DOMParser()
      const doc = parser.parseFromString(this.searchData, 'text/html')
      const searchResultElement = doc.querySelector('.js-predictive-search')

      if (!searchResultElement) return

      const container = this.searchResultContainerRef.value
      const searchResultCount = searchResultElement.querySelector('.js-predictive-search-count')?.dataset?.amount

      if (searchResultCount && Number(searchResultCount) >0 ) {
        container.innerHTML = ''
        container.appendChild(searchResultElement)

        this.updateProductCards()
      } else {
        this.handleNoResult(container)
      }
    }
  }

  async handleNoResult(container) {
    try {
      const response = await fetch(
        `/search?q=${encodeURIComponent(this.searchTerm)}&section_id=${window.main_suggested_result_section_id}`,
      )
      if (!response.ok) {
        console.error(`HTTP error! Status: ${response.status}`)
        console.error(`HTTP error! Reason: ${response}`)
        container.innerHTML = ''
        return
      }
      const data = await response.text()
      const parser = new DOMParser()
      const doc = parser.parseFromString(data, 'text/html')
      const noResultElement = doc.querySelector('.js-suggested-results')
      container.innerHTML = ''
      container.appendChild(noResultElement)

      this.updateProductCards()
    } catch (error) {
      console.error('Error:', error.message)
      container.innerHTML = ''
      return
    }
  }

  updateProductCards() {
    const container = this.searchResultContainerRef.value

    const cards = container.querySelectorAll('nova-product-card')
    for (const card of cards) {
      const variantsTemplate = container.querySelector(
        `#async-variants-json-${card.id}`,
      )
      const variants = variantsTemplate
        ? JSON.parse(variantsTemplate.content.textContent.trim())
        : []
      card.variants = variants

      const swatchesTemplate = container.querySelector(
        `#async-swatches-json-${card.id}`,
      )
      const swatches = swatchesTemplate
        ? JSON.parse(swatchesTemplate.content.textContent.trim())
        : []
      card.swatches = swatches

      const sizesTemplate = container.querySelector(
        `#async-sizes-json-${card.id}`,
      )
      const sizes = sizesTemplate
        ? JSON.parse(sizesTemplate.content.textContent.trim())
        : []
      card.sizes = sizes

      const variantUrlsTemplate = container.querySelector(
        `#async-variant-urls-json-${card.id}`,
      )
      const variantUrls = variantUrlsTemplate
        ? JSON.parse(variantUrlsTemplate.content.textContent.trim())
        : []
      card.variantUrls = variantUrls
    }
  }

  render() {
    return html`
      <div ${ref(this.searchResultContainerRef)}></div>
    `
  }

   static styles = [
    styles.flex,
    styles.variables,
    styles.typography,
    // When container.appendChild(searchResultElement)
    css`
      .predictive-search mark {
        background-color: inherit;
        font: var(--nova-body-font);
      }

      .predictive-search a:hover {
        text-decoration: underline;
      }

      .predictive-search a {
        text-decoration: none;
        color: inherit;
        font: var(--nova-body-font);
      }

      .predictive-search span {
        color: inherit;
        font: var(--nova-body-font);
      }

      .predictive-search h2 {
        margin: 0;
      }

      .predictive-search__content {
        padding: var(--nova-space-sm) var(--nova-space-2xs) var(--nova-space-lg-1);
        flex-direction: column;
        row-gap: var(--nova-space-sm);
      }

      .predictive-search__footer-link {
        display: block;
        padding: var(--nova-space-2xs) var(--nova-space-sm);
        background-color: var(--nova-brand-ink);
        text-align: center;
      }

      .predictive-search .predictive-search__footer-link {
        color: var(--nova-color-white);
      }

      .predictive-search__queries,
      .predictive-search__products,
      .predictive-search__collections {
        row-gap: var(--nova-space-sm);
        flex: 1;

        & h2 {
          display: none;
        }
      }

      .predictive-search__queries-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-column-gap: var(--nova-space-sm);
        grid-row-gap: var(--nova-space-2xs);
      }

      .predictive-search__items,
      .predictive-search__products h2 {
        padding: 0 var(--nova-space-2xs);
      }

      .predictive-search__collections {
        display: none;
      }

      .predictive-search__products-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-column-gap: var(--nova-space-2xs);
        grid-row-gap: var(--nova-space-lg-1);

        & .predictive-search__product-card {
          & nova-product-card {
            padding-bottom: 0;

            &::part(product-header-element) {
              margin-bottom: 0;
              flex-direction: column;
              gap: var(--nova-space-2xs);

              @media screen and (min-width: 768px) {
                flex-direction: column;
                gap: var(--nova-space-xs-2);
              }
            }

            &::part(product-title-element) {
              display: block;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
      }

      @media screen and (min-width: 768px) {
        .predictive-search__queries,
        .predictive-search__products,
        .predictive-search__collections {
          row-gap: var(--nova-space-xs-1);

          & h2 {
            display: block;
          }
        }
      }

      @media screen and (min-width: 990px) {
        .predictive-search__content {
          padding: var(--nova-space-md) var(--nova-space-xs-1);
          display: flex;
          flex-direction: column;
          row-gap: 48px;
        }

        .predictive-search__items {
          padding: 0;
        }

        .predictive-search__products h2 {
          padding: 0 var(--nova-space-xs-1);
        }

        .predictive-search__items {
          display: flex;
          flex-direction: row;
          column-gap: 48px;
        }

        .predictive-search__collections {
          display: flex;
        }

        .predictive-search__queries,
        .predictive-search__collections {
          padding: 0 var(--nova-space-xs-1);
        }

        .predictive-search__collections-list,
        .predictive-search__queries-list {
          display: flex;
          flex-direction: column;
          row-gap: var(--nova-space-2xs);
        }

        .predictive-search__products-list {
          grid-template-columns: repeat(4, 1fr);
          grid-column-gap: var(--nova-space-xs-1);
          grid-row-gap: var(--nova-space-xs-1);
        }
      }

      @media screen and (min-width: 1200px) {
        .predictive-search__products-with-2-columns {
          max-width: 67%;
          flex: 1;
        }

        .predictive-search__products-with-1-columns {
          max-width: 80%;
        }

        .predictive-search__products-with-0-columns {
          max-width: 100%;
        }

        .predictive-search__content {
          flex-direction: row;
          column-gap: 48px;
          justify-content: space-between;
        }
      }

      .predictive-search__product-card {
        width: 100%;
        min-width: 0;
        justify-self: stretch;
      }
    `,
    // When no results
    css`
      .suggested-results {
        padding: var(--nova-space-sm);
        flex-direction: column;
        row-gap: var(--nova-space-lg);
      }

      .suggested-results a {
        text-decoration: none;
      }

      .suggested-results a:hover {
        text-decoration: underline;
        font-weight: bolder;
        color: var(--nova-brand-ink);
      }

      .suggested-results p,
      .suggested-results h2 {
        margin: 0;
      }

      .suggested-results__collection {
        color: var(--nova-color-grey-400);
      }

      .suggested-results__header {
        row-gap: 5px;
      }

      .suggested-results__header strong {
        font-weight: bolder;
      }

      .suggested-results__sub-header {
        font-size: 12px;
        line-height: 1.4;
        color: var(--nova-color-subdued);
      }

      .suggested-results__content {
        row-gap: 30px;
      }

      .suggested-results__collections,
      .suggested-results__products {
        row-gap: var(--nova-space-2xs);
      }

      .suggested-results__collections-list {
        row-gap: var(--nova-space-2xs);
        column-gap: var(--nova-space-sm);
        flex-wrap: wrap;
        max-height: 99px;
      }

      .suggested-results__products-list {
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: auto;
        grid-column-gap: var(--nova-space-2xs);
        grid-row-gap: var(--nova-space-2xs);
      }

      @media screen and (min-width: 768px) {
        .suggested-results__collections-list {
          column-gap: var(--nova-space-xs-1);
        }
      }

      @media screen and (min-width: 990px) {
        .suggested-results {
          padding: var(--nova-space-lg-1);
        }

        .suggested-results__products-list {
          grid-template-columns: repeat(4, 1fr);
        }
      }

      @media screen and (min-width: 1200px) {
        .suggested-results {
          flex-direction: row;
          justify-content: space-between;
        }

        .suggested-results__products {
          max-width: 70%;
          flex: 1;
        }

        .suggested-results__collections-list {
          max-height: unset;
        }
      }

      .suggested-results__product-card {
        width: 100%;
        min-width: 0;
        justify-self: stretch;
      }
    `
   ]
}

customElements.define('nova-search-result', NovaSearchResult)
